import { createFileRoute, Link } from "@tanstack/react-router";
import { trackConversion } from "@/lib/gtag";
import { Phone, Route as RouteIcon, Clock, Luggage, ShieldCheck, Car } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { StickyMobileBar } from "@/components/StickyMobileBar";
import { IdleCallNudge } from "@/components/IdleCallNudge";

const URL = "https://sepremiumcabs.com/long-distance-taxi";
const TITLE = "Long Distance Taxi Melbourne | Country & Regional Trips";
const DESC =
  "Long distance taxi from Melbourne to regional Victoria — Geelong, Ballarat, Bendigo, Mornington Peninsula and Avalon Airport. Fixed quote before you travel. Call 0493 100 265.";

const FAQS = [
  {
    q: "Do you do long distance taxi trips out of Melbourne?",
    a: "Yes. We run pre-booked long distance trips from Melbourne to regional Victoria and back, including Geelong, Ballarat, Bendigo, the Mornington Peninsula and Avalon Airport.",
  },
  {
    q: "How is a long distance fare worked out?",
    a: "Long trips are quoted as a fixed fare before you travel, based on the pickup and destination. Call 0493 100 265 with both addresses and we will quote it upfront.",
  },
  {
    q: "How far ahead should I book?",
    a: "As early as you can. Long trips take a vehicle off local work for several hours, so booking a day or more ahead gives the best chance of your preferred pickup time.",
  },
  {
    q: "What vehicle should I choose for a long trip?",
    a: "Premium Sedan seats up to 4 with 3 bags, Premium SUV up to 6 with 5 bags, and Maxi up to 11 with 9 bags — pick on luggage as much as passenger count for country trips.",
  },
];

const DESTINATIONS = [
  { name: "Geelong", note: "Down the Princes Freeway — common for cruise, uni and family trips." },
  { name: "Avalon Airport", note: "Alternative to Tullamarine for Jetstar and interstate flights." },
  { name: "Mornington Peninsula", note: "Mornington, Rosebud, Sorrento and the Peninsula Link corridor." },
  { name: "Ballarat", note: "Western Freeway run for regional appointments and events." },
  { name: "Bendigo", note: "Calder Freeway trip for regional Victoria connections." },
  { name: "Phillip Island", note: "South Gippsland Highway for the island and Cowes." },
  { name: "Yarra Valley", note: "Healesville and Yarra Glen winery day trips." },
  { name: "Warragul & Gippsland", note: "Princes Freeway east for Gippsland towns." },
];

export const Route = createFileRoute("/long-distance-taxi")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://sepremiumcabs.com/og-image.jpg" },
      { name: "twitter:image", content: "https://sepremiumcabs.com/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Long distance taxi",
          name: "Long Distance Taxi from Melbourne",
          description: DESC,
          areaServed: "Victoria, Australia",
          provider: {
            "@type": "LocalBusiness",
            name: "SE Premium Cabs",
            telephone: "+61493100265",
            email: "sepremiumcabs@gmail.com",
            url: "https://sepremiumcabs.com/",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://sepremiumcabs.com/" },
            { "@type": "ListItem", position: 2, name: "Long Distance Taxi", item: URL },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: LongDistancePage,
});

function LongDistancePage() {
  const points = [
    { icon: RouteIcon, title: "Fixed Quote First", body: "Give us both addresses and we quote the whole trip before you commit — no meter anxiety two hours down the freeway." },
    { icon: Car, title: "One Driver, Door to Door", body: "The same driver and vehicle for the whole journey, straight to the address — no transfers or connections." },
    { icon: Luggage, title: "Room for Real Luggage", body: "Sedan, SUV and 11-seat Maxi options so cases, prams and sports gear travel with you, not on your lap." },
    { icon: Clock, title: "Any Hour", body: "Early-morning departures and late returns, 24/7 including public holidays." },
    { icon: ShieldCheck, title: "Accredited Drivers", body: "CPV-accredited drivers used to freeway and country-road driving in all conditions." },
    { icon: Phone, title: "Book by Phone", body: "Long trips are best arranged by phone so we can confirm timing, luggage and the return leg." },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <div className="mx-auto max-w-4xl px-6 py-12 pb-28 lg:py-20 lg:pb-20">
        <nav className="mb-8 text-xs font-semibold tracking-widest text-muted-foreground">
          <Link to="/" className="hover:text-primary">HOME</Link>
          <span className="mx-2">/</span>
          <span className="text-primary">LONG DISTANCE TAXI</span>
        </nav>

        <h1 className="font-serif text-4xl font-bold tracking-tight lg:text-5xl">
          Long Distance Taxi
          <br />
          <span className="text-primary">from Melbourne</span>
        </h1>
        <p className="mt-6 text-base leading-relaxed text-muted-foreground lg:text-lg">
          Travelling beyond the metropolitan area? SE Premium Cabs runs pre-booked long distance
          trips from Melbourne to regional Victoria — one driver, one vehicle, door to door, with
          the fare agreed before you leave. It suits country appointments, cruise and Avalon
          Airport connections, and family trips where trains and coaches do not line up.
        </p>

        <div className="mt-8 flex flex-wrap gap-4">
          <a
            href="tel:0493100265"
            onClick={() => trackConversion("phoneCall")}
            className="flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-bold tracking-wider text-primary-foreground transition-transform hover:scale-[1.02]"
          >
            <Phone className="h-4 w-4" /> CALL FOR A QUOTE
          </a>
          <Link
            to="/"
            hash="book"
            className="flex items-center gap-2 rounded-md border border-primary px-6 py-3 text-sm font-bold tracking-wider text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
          >
            BOOK ONLINE
          </Link>
        </div>

        <section className="mt-14">
          <h2 className="font-serif text-2xl font-bold tracking-tight">Where we regularly travel</h2>
          <div className="mt-2 h-px w-16 bg-primary" />
          <p className="mt-4 text-sm text-muted-foreground">
            These are common destinations — if yours is not listed, call and ask; we quote any
            Victorian trip.
          </p>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {DESTINATIONS.map((d) => (
              <div key={d.name} className="rounded-lg border border-border/60 bg-card p-5">
                <h3 className="text-sm font-bold tracking-wider">{d.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{d.note}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <h2 className="font-serif text-2xl font-bold tracking-tight">Why book a long distance cab</h2>
          <div className="mt-2 h-px w-16 bg-primary" />
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {points.map((p) => (
              <div key={p.title} className="rounded-lg border border-border/60 bg-card p-5">
                <div className="flex items-center gap-3">
                  <p.icon className="h-5 w-5 text-primary" />
                  <h3 className="text-sm font-bold tracking-wider">{p.title}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <h2 className="font-serif text-2xl font-bold tracking-tight">Long distance questions</h2>
          <div className="mt-2 h-px w-16 bg-primary" />
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {FAQS.map((f) => (
              <details key={f.q} className="group rounded-lg border border-border/60 bg-card p-5 open:border-primary/60">
                <summary className="flex cursor-pointer items-center justify-between gap-4 text-sm font-semibold">
                  <span>{f.q}</span>
                  <span className="text-primary transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mt-14 rounded-xl border border-primary/40 bg-card p-8 text-center">
          <h2 className="font-serif text-2xl font-bold tracking-tight">Get a fixed quote for your trip</h2>
          <p className="mt-3 text-sm text-muted-foreground">
            Flying instead? See{" "}
            <Link to="/melbourne-airport-taxi" className="text-primary">Melbourne Airport transfers</Link>. Travelling for work?{" "}
            <Link to="/corporate-taxi" className="text-primary">Corporate taxi accounts</Link>.
          </p>
          <a
            href="tel:0493100265"
            onClick={() => trackConversion("phoneCall")}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-bold tracking-wider text-primary-foreground"
          >
            <Phone className="h-4 w-4" /> CALL 0493 100 265
          </a>
        </section>
      </div>
      <SiteFooter />
      <StickyMobileBar />
      <IdleCallNudge />
    </div>
  );
}
